// DEPENDENCIES ---------------------------------------------------------------- //

import React, { useEffect, useState } from 'react';
import { Container, Paper, Typography, Box, CircularProgress, Divider, Button } from '@mui/material';
import { MdDownload } from 'react-icons/md';
import { marked } from 'marked';
import { css } from '@emotion/css';
import { useUser } from '@store';
import termsService from '@features/auth/services/termsService';
import { getUserAgreement, UserAgreementContent } from '@lib/contentful';
import ContentfulMarkdown from '@components/Contentful/ContentfulMarkdown';
import { toast } from 'react-toastify';

// COMPONENT ---------------------------------------------------------------- //

const UserAgreementPage: React.FC = () => {
  const cls = useStyles();
  const user = useUser();

  const [acceptanceDate, setAcceptanceDate] = useState<string | null>(null);
  const [userAgreementContent, setUserAgreementContent] = useState<UserAgreementContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      // Fetch user agreement content
      try {
        const content = await getUserAgreement();
        setUserAgreementContent(content);
      } catch (err) {
        console.error('Error loading user agreement:', err);
        setError('Failed to load User Agreement content');
        toast.error('Failed to load User Agreement. Please try refreshing the page.');
      }

      // Fetch acceptance date if user is logged in
      if (user?.profile?.id) {
        try {
          const acceptance = await termsService.getTermsAcceptance(user.profile.id);
          if (acceptance?.event_time) {
            setAcceptanceDate(
              new Date(acceptance.event_time).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            );
          }
        } catch (err) {
          console.error('Error loading acceptance date:', err);
        }
      }

      setLoading(false);
    };

    loadData();
  }, [user?.profile?.id]);

  const handleDownload = () => {
    if (!userAgreementContent?.content) return;

    // Create a hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    // Parse markdown to HTML
    const htmlContent = marked(userAgreementContent.content);

    // Set iframe content using srcdoc (avoids deprecated document.write)
    iframe.srcdoc = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>User Agreement V${userAgreementContent.publishedVersion}</title>
          <style>
            body {
              font-family: Arial, Helvetica, sans-serif;
              line-height: 1.6;
              max-width: 800px;
              margin: 40px auto;
              padding: 20px;
              color: #333;
            }
            h1 {
              font-size: 24px;
              font-weight: 700;
              margin-bottom: 16px;
              color: #000;
            }
            h2 {
              font-size: 20px;
              font-weight: 600;
              margin-top: 32px;
              margin-bottom: 12px;
              color: #000;
            }
            h3 {
              font-size: 18px;
              font-weight: 600;
              margin-top: 24px;
              margin-bottom: 10px;
              color: #000;
            }
            p {
              margin-bottom: 12px;
              color: #333;
            }
            ul, ol {
              margin-bottom: 12px;
              padding-left: 24px;
            }
            li {
              margin-bottom: 8px;
            }
            strong {
              font-weight: 600;
              color: #000;
            }
            hr {
              margin: 24px 0;
              border: none;
              border-top: 1px solid #ccc;
            }
            a {
              color: #0066cc;
              text-decoration: none;
            }
            blockquote {
              border-left: 4px solid #0066cc;
              padding-left: 16px;
              margin: 16px 0;
              color: #666;
              font-style: italic;
            }
            code {
              background-color: #f5f5f5;
              padding: 2px 6px;
              border-radius: 3px;
              font-family: 'Courier New', monospace;
              font-size: 0.9em;
            }
            pre {
              background-color: #f5f5f5;
              padding: 12px;
              border-radius: 4px;
              overflow-x: auto;
              margin-bottom: 12px;
            }
            pre code {
              background-color: transparent;
              padding: 0;
            }
            @media print {
              body {
                margin: 0;
                padding: 20px;
              }
            }
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
      </html>
    `;

    // Wait for content to load, then print and cleanup
    iframe.onload = () => {
      iframe.contentWindow?.print();

      // Remove iframe after a delay to allow print dialog to open
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 100);
    };
  };

  if (loading) {
    return (
      <Container maxWidth='md' sx={{ paddingTop: '24px', paddingBottom: '24px' }}>
        <Paper variant='custom' sx={{ padding: '36px 24px', textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant='body1' sx={{ marginTop: 2 }}>
            Loading User Agreement...
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error || !userAgreementContent) {
    return (
      <Container maxWidth='md' sx={{ paddingTop: '24px', paddingBottom: '24px' }}>
        <Paper variant='custom' sx={{ padding: '36px 24px', textAlign: 'center' }}>
          <Typography variant='h6' color='error' gutterBottom>
            {error || 'Failed to load User Agreement'}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Please try refreshing the page or contact support.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth='md' sx={{ paddingTop: '24px', paddingBottom: '24px' }}>
      <Paper variant='custom' sx={{ padding: '36px 24px' }}>
        <Box className={cls.markdownWrapper}>
          <ContentfulMarkdown content={userAgreementContent.content} variant='default' />
        </Box>

        <Divider sx={{ marginY: 4 }} />

        <Box className={cls.acceptanceSection}>
          <Typography variant='h6' gutterBottom>
            Acceptance Information
          </Typography>
          {acceptanceDate ? (
            <Typography variant='body2' color='text.secondary'>
              You accepted this User Agreement on <strong>{acceptanceDate}</strong>
            </Typography>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              No acceptance record found
            </Typography>
          )}

          <Button variant='outlined' startIcon={<MdDownload />} onClick={handleDownload} sx={{ marginTop: 2 }}>
            Download Agreement
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = () => ({
  markdownWrapper: css`
    h2 {
      text-align: center;
      font-size: 1.33em;
    }
  `,

  acceptanceSection: css`
    padding: 16px 0;

    @media print {
      button {
        display: none;
      }
    }
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default UserAgreementPage;
